import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, DollarSign, Calendar, User, Truck, Wrench, FileText, Save, XCircle, Package, Tag } from 'lucide-react';

const ExitForm = ({ currentExit, onSave, onCancel }) => {
  const [exit, setExit] = useState({
    id: null,
    material: '',
    quantity: '',
    unit: '',
    cost: '',
    date: '',
    deliveredTo: '', // Nuevo campo
    requisitionNumber: '', // Nuevo campo
    truck: '',
    workshop: ''
  });

  useEffect(() => {
    if (currentExit) {
      setExit(currentExit);
    } else {
      setExit({
        id: null,
        material: '',
        quantity: '',
        unit: '',
        cost: '',
        date: '',
        deliveredTo: '',
        requisitionNumber: '',
        truck: '',
        workshop: ''
      });
    }
  }, [currentExit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setExit(prevExit => ({
      ...prevExit,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(exit);
  };

  return (
    <motion.div
      className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 md:p-12 w-full max-w-3xl mx-auto border border-gray-200"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 10 }}
    >
      <div className="flex flex-col items-center mb-8">
        <motion.div
          className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full shadow-lg mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, damping: 10, delay: 0.1 }}
        >
          <Package className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          {currentExit ? 'Editar Salida' : 'Registrar Nueva Salida'}
        </h2>
        <p className="text-gray-500 text-center">
          {currentExit ? 'Modifica los detalles de esta salida de material.' : 'Introduce los detalles de la nueva salida de material.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
          <Box className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="material"
            placeholder="Material Entregado"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.material}
            onChange={handleChange}
            required
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="number"
            name="cost"
            placeholder="Costo del Material"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.cost}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
          <Box className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="number"
            name="quantity"
            placeholder="Cantidad"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.quantity}
            onChange={handleChange}
            required
            min="0"
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
          <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="unit"
            placeholder="Unidad (ej. piezas, litros)"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.unit}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.6 }}>
          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="date"
            name="date"
            placeholder="Fecha de Salida"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.date}
            onChange={handleChange}
            required
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="deliveredTo"
            placeholder="Se Entregó A (Nombre del Solicitante)"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.deliveredTo}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.8 }}>
          <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="requisitionNumber"
            placeholder="No. de Vale / Requisición"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.requisitionNumber}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.9 }}>
          <Truck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="truck"
            placeholder="Camión (ej. J-01, J-512)"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.truck}
            onChange={handleChange}
          />
        </motion.div>

        <motion.div className="relative md:col-span-2" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 1.0 }}>
          <Wrench className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            name="workshop"
            placeholder="Taller (ej. Taller 1, Taller 2)"
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all duration-200 text-gray-800"
            value={exit.workshop}
            onChange={handleChange}
          />
        </motion.div>

        <div className="md:col-span-2 flex justify-end gap-4 mt-4">
          <motion.button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gray-200 text-gray-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <XCircle className="w-5 h-5" />
            Cancelar
          </motion.button>
          <motion.button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <Save className="w-5 h-5" />
            {currentExit ? 'Guardar Cambios' : 'Registrar Salida'}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default ExitForm;